import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom'; // Hook para controlar las pestañas por URL

const ServiceDashboard = ({ serviceTitle, serviceDescription, videoUrl, tabsWidgets }) => {
  // 1. Control de pestañas a través de la URL (?tab=nombre-pestana)
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'metodologia';

  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true); // Estado de audio del video
  const videoRef = useRef(null);

  // Forzar autoplay seguro al montar
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
      videoRef.current.play().catch((err) => console.log("Autoplay mitigado:", err));
    }
  }, [videoUrl, isMuted]);

  // Cambiar pestaña actualizando la URL en limpio
  const handleTabChange = (tabId) => {
    setSearchParams({ tab: tabId });
  };

  // Alternar el estado del audio
  const toggleAudio = () => {
    if (videoRef.current) {
      const currentMuteStatus = !videoRef.current.muted;
      videoRef.current.muted = currentMuteStatus;
      setIsMuted(currentMuteStatus);
    }
  };

  return (
    <div className="w-full bg-slate-50 min-h-screen">
      
      {/* SECCIÓN HERO */}
      <section className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 text-white py-16 px-6 lg:px-16 relative overflow-hidden">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
          
          {/* Lado Izquierdo: Datos */}
          <div className="lg:col-span-7 text-left space-y-6">
            <h1 className="text-4xl md:text-6xl font-black tracking-tight leading-tight">
              {serviceTitle}<br />
              <span className="text-yellow-300">Garantizados</span>
            </h1>
            <p className="text-white/90 text-sm md:text-lg font-medium max-w-xl leading-relaxed">
              {serviceDescription}
            </p>
            <div className="pt-2">
              <a href="https://wa.me/573161585420" target="_blank" rel="noreferrer" className="inline-block bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold text-sm px-8 py-4 rounded-xl shadow-lg transition-all transform hover:-translate-y-0.5">
                Solicitar Servicio
              </a>
            </div>
          </div>

          {/* Lado Derecho: Celular Mockup con aviso de sonido interactivo */}
          <div className="lg:col-span-5 flex flex-col items-center lg:items-end relative">
            <div className="w-full max-w-[280px] bg-slate-950 rounded-[2.5rem] p-3 shadow-2xl border-4 border-slate-900 relative aspect-[9/16] overflow-hidden group">
              
              {/* Loader */}
              {isVideoLoading && (
                <div className="absolute inset-0 bg-slate-950 flex items-center justify-center z-20">
                  <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}

              <video
                ref={videoRef} 
                src={videoUrl} 
                className="w-full h-full object-cover rounded-[2rem] cursor-pointer"
                loop 
                muted={isMuted} 
                playsInline 
                autoPlay 
                onClick={toggleAudio}
                onLoadedData={() => setIsVideoLoading(false)}
              />

              {/* AVISO FLOTANTE: Dale para reproducir sonido (Solo si está silenciado y ya cargó) */}
              {isMuted && !isVideoLoading && (
                <button 
                  onClick={toggleAudio}
                  className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/80 backdrop-blur-md text-white text-[11px] font-black px-4 py-2.5 rounded-full flex items-center gap-2 animate-bounce shadow-xl z-30 border border-white/10 whitespace-nowrap uppercase tracking-wider"
                >
                  <svg className="w-3.5 h-3.5 fill-current text-yellow-300 animate-pulse" viewBox="0 0 24 24">
                    <path d="M13.5 4.07v15.86c-1.15-.45-2.17-1.19-3-2.14L6.41 14H3V10h3.41L10.5 6.21c.83-.95 1.85-1.69 3-2.14M12 2L6.41 7.59H2v8.82h4.41L12 22V2z"/>
                  </svg>
                  Activar Sonido
                </button>
              )}

              {/* Pequeño indicador flotante arriba si ya tiene sonido activo */}
              {!isMuted && (
                <button 
                  onClick={toggleAudio}
                  className="absolute top-4 right-4 bg-black/40 hover:bg-black/60 p-2 rounded-full text-white backdrop-blur-sm z-30 transition-colors"
                  aria-label="Silenciar video"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                  </svg>
                </button>
              )}

            </div>
          </div>

        </div>
      </section>

      {/* MENÚ DE PESTAÑAS (TABS) */}
      <div className="bg-white border-b border-slate-200 sticky top-20 z-40 shadow-sm">
        <div className="max-w-5xl mx-auto flex justify-between md:justify-start items-center gap-2 md:gap-8 px-4 overflow-x-auto scrollbar-none">
          {[
            { id: 'metodologia', label: 'Metodología', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
            { id: 'condiciones', label: 'Condiciones', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
            { id: 'examenes', label: 'Exámenes Realizados', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
            { id: 'testimonios', label: 'Testimonios', icon: 'M11.48 3.499c.172-.435.789-.435.961 0l1.566 3.965a.5.5 0 00.473.344l4.288.358c.47.039.658.621.314.93L15.93 11.96a.5.5 0 00-.144.444l.951 4.246c.104.464-.383.818-.797.575l-3.712-2.164a.5.5 0 00-.476 0l-3.712 2.164c-.414.243-.9-.111-.797-.575l.951-4.246a.5.5 0 00-.144-.444L2.831 10.1c-.344-.31-.155-.892.314-.93l4.288-.358a.5.5 0 00.473-.344l1.566-3.965z' }
          ].map((tab) => (
            <button
              key={tab.id} 
              onClick={() => handleTabChange(tab.id)} // Llama al nuevo controlador URL
              className={`flex items-center gap-2 py-4 px-2 text-xs md:text-sm font-bold border-b-2 transition-all whitespace-nowrap ${activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} /></svg>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* RECUADRO DE CONTENIDO REACIVÓ */}
      <div className="max-w-5xl mx-auto py-12 px-4">
        {tabsWidgets && tabsWidgets[activeTab]}
      </div>

    </div>
  );
};

export default ServiceDashboard;